import {
  CENTRAL_AUTH_PAGE_PATHS,
  CENTRAL_SIGN_IN_PATH,
  SIGN_IN_PARAMS,
  type CentralSignInClient,
} from '@instanexus/contracts';

/** The query key that carries the address from "forgot" into "reset". */
export const RESET_EMAIL_PARAM = 'email';

/**
 * The query keys of a member-invitation link. They must equal the names `memberInviteAcceptUrl`
 * (API `identity/service.ts`) writes into the emailed link. The token is a capability.
 */
export const INVITE_EMAIL_PARAM = 'email';
export const INVITE_TOKEN_PARAM = 'token';

const withClient = (path: string, client: CentralSignInClient, email?: string): string => {
  const params = new URLSearchParams({ [SIGN_IN_PARAMS.client]: client });
  if (email !== undefined && email !== '') params.set(RESET_EMAIL_PARAM, email);
  return `${path}?${params.toString()}`;
};

/** '/login?client=admin' (+ '&email=…' when given and non-empty). */
export function centralLoginPath(client: CentralSignInClient, email?: string): string {
  return withClient(CENTRAL_SIGN_IN_PATH, client, email);
}

/** '/forgot-password?client=admin' */
export function forgotPasswordPath(client: CentralSignInClient): string {
  return withClient(CENTRAL_AUTH_PAGE_PATHS.forgotPassword, client);
}

/** '/reset-password?client=admin&email=ada%40northwind.test'; email omitted when ''. */
export function resetPasswordPath(client: CentralSignInClient, email: string): string {
  return withClient(CENTRAL_AUTH_PAGE_PATHS.resetPassword, client, email);
}

/** Query keys the verification screens read; the values are typed by the person, never secret. */
export const VERIFY_EMAIL_PARAM = 'email';
export const VERIFY_PHONE_PARAM = 'phone';

/** '/verify-email?email=…' ('/verify-email' when email is ''). */
export function verifyEmailPath(email: string): string {
  if (email.length === 0) return CENTRAL_AUTH_PAGE_PATHS.verifyEmail;
  const search = new URLSearchParams({ [VERIFY_EMAIL_PARAM]: email }).toString();
  return `${CENTRAL_AUTH_PAGE_PATHS.verifyEmail}?${search}`;
}

/** '/verify-phone?email=…&phone=…'; each half omitted when ''. */
export function verifyPhonePath(email: string, phone: string): string {
  const params = new URLSearchParams();
  if (email.length > 0) params.set(VERIFY_EMAIL_PARAM, email);
  if (phone.length > 0) params.set(VERIFY_PHONE_PARAM, phone);
  const search = params.toString();
  return search.length === 0
    ? CENTRAL_AUTH_PAGE_PATHS.verifyPhone
    : `${CENTRAL_AUTH_PAGE_PATHS.verifyPhone}?${search}`;
}

/**
 * The phone signup hands verify-email through router LOCATION STATE (not the URL: verify-email
 * only passes it on). Read defensively; '' when absent or malformed.
 */
export function signupHandoffPhone(state: unknown): string {
  if (typeof state !== 'object' || state === null || !('phone' in state)) return '';
  const { phone } = state as { phone: unknown };
  return typeof phone === 'string' ? phone : '';
}
